import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import APPCONSTANTS from '../../constants/appConstants';
import { PROTECTED_ROUTES } from '../../constants/route';
import { getRegionDetailSelector } from '../../store/region/selectors';
import { closeNudge, fetchNudgeStatusRequest, setSiteDetail } from '../../store/site/actions';
import { getSiteListSelector, nudgeLoadingSelector, nudgeStatusSelector } from '../../store/site/selectors';
import { roleSelector } from '../../store/user/selectors';
import Loader from '../loader/Loader';
import Modal from '../modal/ModalForm';
import sessionStorageServices from '../../global/sessionStorageServices';

interface INudgeProps {
  onClose?: () => void;
}

const Nudge: React.FC<INudgeProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  // Selectors
  const nudgeStatus = useSelector(nudgeStatusSelector);
  const isLoading = useSelector(nudgeLoadingSelector);
  const region = useSelector(getRegionDetailSelector);
  const userRole = useSelector(roleSelector);
  const siteList = useSelector(getSiteListSelector);

  // Refs
  const apiCallMade = useRef(false);

  // Extract tenant IDs from the site list
  const tenantIds = siteList.map((site) => Number(site.tenantId)).filter(Boolean);
  const isHF4ReportUser = userRole === APPCONSTANTS.ROLES.HF4_REPORT_USER;
  const canShowNudge = isHF4ReportUser && tenantIds.length > 0;

  // Get the format for last month (e.g., "Jan2023")
  const getLastMonthFormat = useCallback(() => {
    const now = new Date();
    const prevMonthIndex = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const monthLabel = APPCONSTANTS.MONTH[prevMonthIndex].label;
    return `${monthLabel}${year}`;
  }, []);

  // Mark nudge as shown and close it
  const markNudgeAsShown = useCallback(() => {
    sessionStorage.setItem('nudgeShown', 'true');
    onClose?.();
  }, [onClose]);

  // Handle API call for nudge status
  useEffect(() => {
    // Skip if nudge already shown in this session
    // Skip if API call already made in this component lifecycle
    if (!canShowNudge || sessionStorage.getItem('nudgeShown') || apiCallMade.current) {
      return;
    }
    // Mark that we've made the API call
    apiCallMade.current = true;

    // Fetch nudge status
    dispatch(
      fetchNudgeStatusRequest({
        hf4User: isHF4ReportUser,
        key: getLastMonthFormat(),
        tenantIds,
        successCb: (data) => {
          if (data.showNudge === false) {
            markNudgeAsShown();
          }
        }
      })
    );
  }, [dispatch, canShowNudge, isHF4ReportUser, tenantIds, getLastMonthFormat, markNudgeAsShown]);

  // Handle cancel button click
  const handleCancel = useCallback(() => {
    dispatch(closeNudge());
    markNudgeAsShown();
  }, [dispatch, markNudgeAsShown]);

  // Handle submit button click
  const handleSubmit = useCallback(() => {
    const { showNudge, healthFacilityId, healthFacilityName, tenantId } = nudgeStatus;
    if (showNudge && healthFacilityId && healthFacilityName && tenantId) {
      // Set site details in the store
      sessionStorageServices.setItem(APPCONSTANTS.SITE_ID, healthFacilityId);
      sessionStorageServices.setItem(APPCONSTANTS.SITE_TENANT_ID, tenantId);
      dispatch(
        setSiteDetail({
          id: healthFacilityId,
          name: healthFacilityName,
          tenantId
        })
      );

      // Navigate to CHW list
      dispatch(closeNudge());
      const monthYear = getLastMonthFormat();
      const url = PROTECTED_ROUTES.chwList
        .replace(':regionId', region.id.toString())
        .replace(':regionName', region.name || '')
        .replace(':siteId', healthFacilityId.toString())
        .replace(':siteName', (healthFacilityName || '').replace(/\//g, ''))
        .replace(':reportType', 'HF4')
        .replace(':monthYear', monthYear);

      history.push(url);
      markNudgeAsShown();
    } else {
      handleCancel();
    }
  }, [nudgeStatus, dispatch, getLastMonthFormat, history, region.id, region.name, handleCancel, markNudgeAsShown]);

  // Render nudge content
  const renderNudgeContent = useCallback(
    () => (
      <div className='px-2 py-1'>
        <p className='text-lg'>{nudgeStatus.nudgeMessage}</p>
      </div>
    ),
    [nudgeStatus.nudgeMessage]
  );

  // Early return if user is not HF4_REPORT_USER
  if (!isHF4ReportUser) {
    return null;
  }

  return (
    <>
      {isLoading && <Loader />}
      <Modal
        show={nudgeStatus.showNudge}
        title={APPCONSTANTS.NUDGE.TITLE}
        handleCancel={handleCancel}
        handleFormSubmit={handleSubmit}
        size='modal-md'
        autoHeight={true}
        submitText={APPCONSTANTS.NUDGE.OK_BUTTON}
        cancelText={APPCONSTANTS.NUDGE.CANCEL_BUTTON}
        render={renderNudgeContent}
      />
    </>
  );
};

export default memo(Nudge);
